import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import { PlatformIcon } from '../components/PlatformIcons';
import { AutoplayVideo } from '../components/AutoplayVideo';
import { cookieConsent } from '../lib/cookie-consent';
import { 
  Shield, 
  Zap, 
  Users, 
  BarChart3, 
  CheckCircle, 
  ArrowRight,
  Star,
  Award,
  Globe,
  Sparkles,
  Upload,
  FileImage,
  FileVideo,
  X,
  Play,
  Eye,
  Target,
  TrendingUp,
  Brain,
  Lightbulb,
  Gauge,
  Rocket,
  Lock,
  Clock,
  DollarSign,
  AlertTriangle,
  ThumbsDown,
  ThumbsUp,
  Layers,
  FolderOpen,
  Building2,
  FileText,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  MessageSquare,
  Mail,
  Minus,
  Plus,
  ArrowUp,
  History,
  GitBranch,
  Video,
  Image,
  Palette,
  FileSearch,
  RotateCcw,
  CreditCard
} from 'lucide-react';

// reCAPTCHA v3 global declaration
declare global {
  interface Window {
    grecaptcha: any;
  }
}

export function LandingPage() {
  const navigate = useNavigate();
  
  // Hide reCAPTCHA badge
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .grecaptcha-badge {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showPreviewResults, setShowPreviewResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [testimonialAutoRotate, setTestimonialAutoRotate] = useState(true);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeWorkflowTab, setActiveWorkflowTab] = useState<'create' | 'analyze'>('create');
  const [expandedPricingPlans, setExpandedPricingPlans] = useState<Set<string>>(new Set());
  
  // Toggle pricing plan expansion
  const togglePricingPlan = (planName: string) => {
    setExpandedPricingPlans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planName)) {
        newSet.delete(planName);
      } else {
        newSet.add(planName);
      }
      return newSet;
    });
  };
  
  // Animated headline state
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    category: 'general',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Mobile optimization: Reduce motion and improve performance
  const prefersReducedMotion = useReducedMotion();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Animated headline phrases
  const headlinePhrases = [
    { text: "That Never Get Rejected", icon: Shield },
    { text: "In Under 3 Minutes", icon: Clock },
    { text: "For 6 Major Platforms", icon: Globe },
    { text: "With AI Enhancement", icon: Sparkles },
    { text: "That Boost Performance", icon: TrendingUp },
    { text: "With 98% Approval Rate", icon: Award }
  ];

  // Mobile-optimized animation settings
  const mobileAnimationConfig = {
    initial: { opacity: 0, y: isMobile ? 15 : 30 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      duration: prefersReducedMotion ? 0 : (isMobile ? 0.4 : 0.8),
      ease: [0.25, 0.1, 0.25, 1] // Optimized easing for mobile
    },
    viewport: { 
      once: true, 
      margin: isMobile ? "-50px" : "-100px", // Trigger animations earlier on mobile
      amount: isMobile ? 0.1 : 0.3 // Less strict visibility requirements on mobile
    }
  };

  // Helper function to get optimized animation props with delay
  const getAnimationProps = (delay = 0) => ({
    initial: mobileAnimationConfig.initial,
    whileInView: mobileAnimationConfig.animate,
    transition: { 
      ...mobileAnimationConfig.transition, 
      delay: prefersReducedMotion ? 0 : delay 
    },
    viewport: mobileAnimationConfig.viewport
  });

  // Optimized scroll detection with throttling for mobile performance
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setShowBackToTop(scrollY > 500);
          setIsScrolled(scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (!testimonialAutoRotate) return;
    
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonialAutoRotate]);

  // Animated headline rotation
  useEffect(() => {
    if (prefersReducedMotion || isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % headlinePhrases.length);
    }, isMobile ? 3000 : 4000); // Faster on mobile
    
    return () => clearInterval(interval);
  }, [prefersReducedMotion, isHovered, isMobile, headlinePhrases.length]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Load reCAPTCHA v3
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  useEffect(() => {
    // Check if site key is configured
    if (!import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
      console.error('VITE_RECAPTCHA_SITE_KEY is not configured');
      return;
    }
    
    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="recaptcha"]`);
    if (existingScript) {
      setRecaptchaLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    
    script.onload = () => {
      // Wait for grecaptcha to be ready
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      }
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
      setRecaptchaLoaded(false);
    };
    
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const scriptToRemove = document.querySelector(`script[src*="recaptcha"]`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Contact form handlers
  const handleContactFormChange = (field: string, value: string) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
    // Clear submit status when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: '' });
    }
  };

  const validateContactForm = () => {
    if (!contactForm.name.trim()) return 'Name is required';
    if (!contactForm.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) return 'Please enter a valid email';
    if (!contactForm.subject.trim()) return 'Subject is required';
    if (!contactForm.message.trim()) return 'Message is required';
    if (contactForm.message.length < 10) return 'Message must be at least 10 characters';
    return null;
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const error = validateContactForm();
    if (error) {
      setSubmitStatus({ type: 'error', message: error });
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitStatus({ type: null, message: '' });

      // Execute reCAPTCHA v3
      let recaptchaToken = '';
      
      if (!recaptchaLoaded) {
        console.warn('reCAPTCHA not loaded yet, proceeding without token');
      } else {
        try {
          recaptchaToken = await new Promise<string>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('reCAPTCHA timeout'));
            }, 5000);
            
            if (window.grecaptcha && window.grecaptcha.ready) {
              window.grecaptcha.ready(() => {
                window.grecaptcha.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, {
                  action: 'contact_form'
                }).then((token) => {
                  clearTimeout(timeout);
                  console.log('reCAPTCHA token obtained successfully');
                  resolve(token);
                }).catch((error) => {
                  clearTimeout(timeout);
                  reject(error);
                });
              });
            } else {
              clearTimeout(timeout);
              reject(new Error('reCAPTCHA not available'));
            }
          });
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          // Continue without reCAPTCHA token if it fails
        }
      }

      // Import EmailService dynamically
      const { EmailService } = await import('../lib/email-service');

      const categories = {
        general: 'General Inquiry',
        technical: 'Technical Support',
        billing: 'Billing Question',
        feature: 'Feature Request',
        bug: 'Bug Report'
      };

      await EmailService.send({
        to: 'support@rulevia.com',
        template: 'support-request' as any,
        data: {
          userName: contactForm.name,
          userEmail: contactForm.email,
          userId: 'visitor',
          category: contactForm.category,
          categoryLabel: categories[contactForm.category as keyof typeof categories] || 'General Inquiry',
          subject: contactForm.subject,
          message: contactForm.message,
          planName: 'Visitor',
          sentAt: new Date().toISOString(),
          source: 'Landing Page'
        },
        recaptchaToken
      });

      setSubmitStatus({
        type: 'success',
        message: 'Thank you! We\'ll respond within 24 hours.'
      });
      
      // Reset form
      setContactForm({
        name: '',
        email: '',
        category: 'general',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or email us directly at support@rulevia.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pain points that marketers face
  const painPoints = [
    {
      icon: AlertTriangle,
      title: "Ad Rejections Cost Money",
      description: "Every rejected ad means wasted time, delayed campaigns, and lost revenue opportunities.",
      stat: "73% of ads get rejected on first submission"
    },
    {
      icon: Clock,
      title: "Manual Compliance Takes Hours",
      description: "Checking each platform's guidelines manually is time-consuming and error-prone.",
      stat: "3+ hours per creative across all platforms"
    },
    {
      icon: DollarSign,
      title: "Expensive & Risky Ad Creation",
      description: "Designing ads from scratch costs $500-2000 per creative, and without performance predictions, you're gambling thousands more on underperforming campaigns.",
      stat: "$1000s wasted on design + poor performance"
    }
  ];

  // Enhanced features with all capabilities
  const features = [
    {
      icon: Sparkles,
      title: "AI Ad Generator",
      description: "Create professional ads in minutes using AI. Just provide your brief, logo, and reference images.",
      highlight: true
    },
    {
      icon: Shield,
      title: "Multi-Platform Compliance",
      description: "Instant compliance checking across Meta, Google, TikTok, YouTube, Pinterest, and Microsoft Ads.",
      highlight: true
    },
    {
      icon: Brain,
      title: "AI Performance Predictions",
      description: "Know exactly how your ads will perform before spending a dollar. Get platform-specific scores.",
      highlight: true
    },
    {
      icon: TrendingUp,
      title: "Ad Enhancement & Optimization",
      description: "AI-powered suggestions and automatic creative improvements for better performance.",
      highlight: true
    },
    {
      icon: GitBranch,
      title: "Version Control",
      description: "Track every iteration of your creatives with complete version history and rollback options."
    },
    {
      icon: FolderOpen,
      title: "Project Management",
      description: "Organize campaigns, track progress, and manage multiple creative projects effortlessly."
    },
    {
      icon: Building2,
      title: "Organization Management",
      description: "Add team members, set permissions, and collaborate across your entire organization."
    },
    {
      icon: FileText,
      title: "Export Analysis Reports",
      description: "Download detailed analysis reports in multiple formats (PDF, CSV, JSON) for clients and stakeholders."
    }
  ];

  const platforms = [
    { name: "Meta", key: "meta" },
    { name: "Google Ads", key: "google" },
    { name: "TikTok", key: "tiktok" },
    { name: "YouTube", key: "youtube" },
    { name: "Pinterest", key: "pinterest" },
    { name: "Microsoft Ads", key: "microsoft" }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: "Alex",
      role: "Marketing Lead",
      content: "I've been running campaigns for fitness brands for years, and Rulevia has cut my ad creation time in half. Uploading a brief and logo generates professional designs instantly, and the AI enhancements fix issues like color contrast on the fly. Our TikTok and Pinterest ads now perform 2x better in CTR, thanks to the predictions. It's intuitive and has made team handoffs seamless with version control—no more lost files!",
      avatar: "AL",
      rating: 5
    },
    {
      name: "Jordan",
      role: "Digital Strategist",
      content: "As a solo marketer for a food startup, I love how Rulevia handles multi-platform compliance without the hassle. I analyzed a video ad in minutes, got optimization suggestions that improved engagement on YouTube, and regenerated variations with feedback until it was perfect. We've seen a 35% uplift in conversions, and the pricing fits my budget perfectly. This tool feels like it was made for small teams like mine.",
      avatar: "JO",
      rating: 5
    },
    {
      name: "Taylor",
      role: "Ad Operations Manager",
      content: "Rulevia's AI generator is a powerhouse for enterprise-level campaigns. We create unlimited variations for A/B testing across Google and Meta, and the performance scores help us predict winners before launch. Collaboration features let our global team work in real-time, tracking every change. It's saved us thousands in design costs and boosted ROI by 4.5x in just two months—game-changing stuff.",
      avatar: "TA",
      rating: 5
    },
    {
      name: "Casey",
      role: "Freelance Marketer",
      content: "I switched to Rulevia after struggling with manual checks on Pinterest and Microsoft Ads. The 3-minute analysis spots compliance issues instantly, and AI tweaks make my creatives pop. Generating ads from scratch with custom styles has helped me deliver faster for clients, with a 40% improvement in click rates. The free trial hooked me, and now I'm on the Pro plan—totally worth it for the efficiency.",
      avatar: "CA",
      rating: 5
    },
    {
      name: "Morgan",
      role: "Campaign Director",
      content: "Our team used to waste hours on versioning and feedback loops, but Rulevia's history tracking and rollback options keep everything organized. AI predictions guided us to optimize for better CTR on Facebook, turning underperforming ads into hits. We've analyzed over 100 creatives this quarter with 90% time savings. It's reliable, user-friendly, and has elevated our entire workflow.",
      avatar: "MO",
      rating: 5
    },
    {
      name: "Riley",
      role: "Performance Analyst",
      content: "Rulevia stands out for its enhancement tools—automatic optimizations like text placement have made our Instagram and TikTok ads convert like crazy. The platform-specific scores let us tweak before going live, avoiding costly mistakes. Collaboration is a breeze with unlimited team access, and we've seen a 3x ROI boost in recent campaigns. If you're in e-commerce, this is your secret weapon.",
      avatar: "RI",
      rating: 5
    },
    {
      name: "Jamie",
      role: "Marketing Consultant",
      content: "I handle ads for multiple clients, and Rulevia's all-in-one compliance for YouTube and Google saves me from endless guideline reviews. Generating ads in seconds with regeneration based on my input is brilliant, and the project management keeps things on track. Our average performance score jumped to 85%, leading to stronger results. Affordable and powerful—I'm a convert!",
      avatar: "JA",
      rating: 5
    },
    {
      name: "Samir",
      role: "Growth Marketer",
      content: "Rulevia has revolutionized how we predict and optimize ads for Meta and Microsoft. The AI suggestions turn good creatives into great ones, and version history prevents any mix-ups in our fast-paced team. We've cut manual work by 95% and seen a 50% performance lift across campaigns. Starting with the Starter plan was easy, and scaling up has been seamless.",
      avatar: "SA",
      rating: 5
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: "How accurate is Rulevia's compliance checking?",
      answer: "Rulevia achieves 98% accuracy in compliance detection across all platforms. Our AI is continuously updated with the latest platform guidelines and policies."
    },
    {
      question: "Can Rulevia really predict ad performance?",
      answer: "Yes! Our AI analyzes thousands of successful campaigns to predict performance metrics. Users typically see a 40-60% improvement in CTR when following our recommendations."
    },
    {
      question: "What file types does Rulevia support?",
      answer: "We support all major image formats (JPG, PNG, GIF, WebP, HEIC, HEIF) up to 20MB and video formats (MP4, MOV, AVI, WebM, MPEG, etc.) up to 1GB. Both static and animated creatives are fully supported."
    },
    {
      question: "How does the creative enhancement feature work?",
      answer: "Our AI analyzes your creative and automatically generates improved versions optimized for each platform's best practices, including proper text placement, color optimization, and composition improvements."
    },
    {
      question: "Can I collaborate with my team?",
      answer: "Absolutely! All plans except Free Trial include team collaboration features. You can invite team members, share projects, and manage permissions from your organization dashboard."
    },
    {
      question: "Is there a limit on analyses per month?",
      answer: "Yes, each plan has a monthly analysis limit: Free Trial (3), Starter (25), Pro (100), Agency (350), and Enterprise (1000). Unused credits roll over up to 25% on paid plans."
    },
    {
      question: "How does the AI Ad Generator work?",
      answer: "Simply provide a brief describing your product and goals, upload your logo and any reference images. Our AI then generates professional ads in your chosen style. You can regenerate with feedback until it's perfect."
    },
    {
      question: "What assets do I need to generate ads?",
      answer: "You need: 1) A brief describing your product/service and target audience, 2) Your logo (optional but recommended), 3) Reference images for style inspiration (optional). That's it! Our AI handles the rest."
    },
    {
      question: "Can I regenerate ads with feedback?",
      answer: "Yes! If the generated ad isn't perfect, provide specific feedback or select improvement areas (colors, layout, text, style, branding) and regenerate. Each regeneration uses 1 credit."
    },
    {
      question: "How many ad variations can I create?",
      answer: "Unlimited! As long as you have credits, you can generate as many variations as needed. Perfect for A/B testing different styles, messages, and layouts to find what performs best."
    }
  ];

  // Before/After comparison data
  const beforeAfterComparison = {
    before: [
      { icon: X, text: "Ad rejected by Meta for 20% text rule violation", color: "text-red-500" },
      { icon: X, text: "45% predicted performance score", color: "text-red-500" },
      { icon: X, text: "3+ hours manual compliance checking", color: "text-red-500" },
      { icon: X, text: "No visibility into why ads fail", color: "text-red-500" },
      { icon: X, text: "$1000s wasted on rejected campaigns", color: "text-red-500" },
      { icon: X, text: "Inconsistent creative versions", color: "text-red-500" },
      { icon: X, text: "Team confusion on latest versions", color: "text-red-500" }
    ],
    after: [
      { icon: CheckCircle, text: "Pre-validated for all platform policies", color: "text-green-500" },
      { icon: CheckCircle, text: "87% performance score with AI optimization", color: "text-green-500" },
      { icon: CheckCircle, text: "3-minute automated analysis", color: "text-green-500" },
      { icon: CheckCircle, text: "Detailed insights and recommendations", color: "text-green-500" },
      { icon: CheckCircle, text: "98% first-time approval rate", color: "text-green-500" },
      { icon: CheckCircle, text: "Complete version history & A/B testing", color: "text-green-500" },
      { icon: CheckCircle, text: "Real-time team collaboration", color: "text-green-500" }
    ],
    metrics: [
      { label: "Time Saved", value: "95%", description: "per creative" },
      { label: "Performance Boost", value: "42%", description: "average increase" },
      { label: "Approval Rate", value: "98%", description: "first submission" },
      { label: "ROI", value: "5.2x", description: "in 3 months" }
    ]
  };

  const pricingPlans = [
    {
      name: 'Free Trial',
      price: 0,
      period: 'month',
      credits: 3,
      creativeCredits: 1,
      features: [
        'All 6 platforms (Meta, Google, TikTok, YouTube, Pinterest, Microsoft)',
        '1 team member',
        '1 active project',
        'Email support'
      ],
      performanceFeature: 'Basic compliance analysis',
      popular: false,
      cta: 'Start Free Trial'
    },
    {
      name: 'Starter',
      price: 15.90,
      period: 'month',
      credits: 25,
      creativeCredits: 5,
      features: [
        'All 6 platforms (Meta, Google, TikTok, YouTube, Pinterest, Microsoft)',
        '1 team member',
        '3 active projects',
        '15 versions per creative',
        'Email support'
      ],
      performanceFeature: 'Detailed analysis reports',
      popular: false,
      cta: 'Start Now'
    },
    {
      name: 'Pro',
      price: 39.90,
      period: 'month',
      credits: 100,
      creativeCredits: 20,
      features: [
        'All 6 platforms (Meta, Google, TikTok, YouTube, Pinterest, Microsoft)',
        '3 team members',
        '10 active projects',
        'Unlimited versions per creative',
        'Advanced reporting & analytics',
        'Team collaboration',
        'Email support'
      ],
      performanceFeature: 'Advanced analytics dashboard',
      popular: true,
      cta: 'Start Now'
    },
    {
      name: 'Agency',
      price: 99.90,
      period: 'month',
      credits: 350,
      creativeCredits: 70,
      features: [
        'All 6 platforms (Meta, Google, TikTok, YouTube, Pinterest, Microsoft)',
        '10 team members',
        '25 active projects',
        'Unlimited versions per creative',
        'Advanced reporting & analytics',
        'Team collaboration',
        'Version history & rollback',
        'Email support'
      ],
      performanceFeature: 'Enterprise analytics',
      popular: false,
      cta: 'Start Now'
    },
    {
      name: 'Enterprise',
      price: 299.90,
      period: 'month',
      credits: 1000,
      creativeCredits: 200,
      features: [
        'All 6 platforms (Meta, Google, TikTok, YouTube, Pinterest, Microsoft)',
        'Unlimited team members',
        'Unlimited active projects',
        'Unlimited versions per creative',
        'Advanced reporting & analytics',
        'Team collaboration',
        'Version history & rollback',
        'Priority support'
      ],
      performanceFeature: 'Custom enterprise features',
      popular: false,
      cta: 'Start Now'
    }
  ];

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    setUploadProgress(0);
    setIsAnalyzing(false);
    setShowPreviewResults(false);
    
    // Simulate file upload
    const uploadInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setIsAnalyzing(true);
          
          // Simulate analysis
          setTimeout(() => {
            setIsAnalyzing(false);
            setShowPreviewResults(true);
            
            // Show registration prompt after preview
            setTimeout(() => {
              setShowRegistrationPrompt(true);
            }, 3000);
          }, 2000);
          
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const resetUploadModal = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    setIsAnalyzing(false);
    setShowRegistrationPrompt(false);
    setShowPreviewResults(false);
  };

  // Navigation handlers for testimonials
  const goToPrevTestimonial = () => {
    setTestimonialAutoRotate(false);
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    // Resume auto-rotate after 10 seconds
    setTimeout(() => setTestimonialAutoRotate(true), 10000);
  };

  const goToNextTestimonial = () => {
    setTestimonialAutoRotate(false);
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    // Resume auto-rotate after 10 seconds
    setTimeout(() => setTestimonialAutoRotate(true), 10000);
  };

  const goToTestimonial = (index: number) => {
    setTestimonialAutoRotate(false);
    setActiveTestimonial(index);
    // Resume auto-rotate after 10 seconds
    setTimeout(() => setTestimonialAutoRotate(true), 10000);
  };

  // Drag handlers for testimonials
  const handleDragStart = () => {
    setIsDragging(true);
    setTestimonialAutoRotate(false);
  };

  const handleDragEnd = (_event: unknown, info: { velocity: { x: number }; offset: { x: number } }) => {
    setIsDragging(false);
    
    const threshold = 100;
    const velocity = info.velocity.x;
    const offset = info.offset.x;
    
    if (Math.abs(velocity) > 500 || Math.abs(offset) > threshold) {
      if (velocity > 0 || offset > threshold) {
        goToPrevTestimonial();
      } else {
        goToNextTestimonial();
      }
    } else {
      // Resume auto-rotate after 10 seconds
      setTimeout(() => setTestimonialAutoRotate(true), 10000);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setTimeout(resetUploadModal, 300);
  };

  return (
    <>
      <SEOHead {...getPageSEO('/')} />
      {/* Mobile Performance Optimization Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            .mobile-sticky-nav {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              width: 100% !important;
              z-index: 9999 !important;
              -webkit-transform: translate3d(0,0,0) !important;
              transform: translate3d(0,0,0) !important;
              -webkit-backface-visibility: hidden !important;
              backface-visibility: hidden !important;
            }
            .group:hover * {
              transform: none !important;
            }
            button:hover {
              transform: none !important;
            }
            .transition-transform {
              transition: none !important;
            }
          }
        `}
      </style>
      
      {/* Navigation - Enhanced Mobile Support */}
      <nav 
        className={`mobile-sticky-nav fixed top-0 left-0 right-0 w-full z-[9999] transition-all duration-300 ${
          isScrolled 
            ? 'bg-black/20 backdrop-blur-xl shadow-xl' 
            : 'bg-black/5 backdrop-blur-lg'
        } ${
          mobileMenuOpen 
            ? 'border-b-0' 
            : 'border-b border-white/10'
        } safe-area-top`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          WebkitTransform: 'translate3d(0,0,0)',
          transform: 'translate3d(0,0,0)',
          backdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)',
          WebkitBackdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="/Logo-Rulevia-White.svg" 
                alt="Rulevia Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#comparison" className="text-gray-300 hover:text-white transition-colors">Results</a>
              <button 
                onClick={() => navigate('/blog')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Blog
              </button>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</a>
              {/* Hidden as requested - Try It Free button
              <button 
                onClick={() => setShowUploadModal(true)}
                className="text-gray-300 hover:text-white transition-colors font-medium flex items-center space-x-2"
              >
                <Target className="w-4 h-4" />
                <span>Try It Free</span>
              </button>
              */}
              <button 
                onClick={() => navigate('/onboarding?mode=login')}
                className="text-gray-300 hover:text-white transition-colors font-medium"
              >
                Login
              </button>
              <button 
                onClick={() => navigate('/onboarding?mode=register')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white p-2 relative z-50"
            >
              <motion.div
                animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </button>
          </div>

        </div>
      </nav>

      {/* Mobile Menu - Separate fixed element with same blur effect as nav */}
      <AnimatePresence mode="wait">
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ 
              duration: 0.2, 
              ease: [0.25, 0.1, 0.25, 1],
              ...(prefersReducedMotion && { duration: 0 })
            }}
            className={`lg:hidden fixed left-0 right-0 w-full z-[9998] border-b border-white/10 transition-all duration-300 ${
              isScrolled 
                ? 'bg-black/20 backdrop-blur-xl shadow-xl' 
                : 'bg-black/5 backdrop-blur-lg'
            }`}
            style={{
              position: 'fixed',
              top: '73px', // Just below the nav bar
              left: 0,
              right: 0,
              zIndex: 9998,
              WebkitTransform: 'translate3d(0,0,0)',
              transform: 'translate3d(0,0,0)',
              backdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)',
              WebkitBackdropFilter: isScrolled ? 'blur(24px)' : 'blur(16px)'
            }}
          >
            <div className="px-4 py-6">
              <div className="flex flex-col space-y-4">
                {[
                  { href: "#features", text: "Features" },
                  { href: "#comparison", text: "Results" },
                  { href: "/blog", text: "Blog", isRoute: true },
                  { href: "#pricing", text: "Pricing" }
                ].map((item, index) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      if (item.isRoute) {
                        e.preventDefault();
                        navigate(item.href);
                      }
                      setMobileMenuOpen(false);
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ 
                      delay: prefersReducedMotion ? 0 : index * 0.05,
                      duration: 0.2 
                    }}
                    className="text-gray-300 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/5"
                  >
                    {item.text}
                  </motion.a>
                ))}
                
                {/* Hidden as requested - Try It Free button
                <motion.button 
                  onClick={() => { setShowUploadModal(true); setMobileMenuOpen(false); }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    delay: prefersReducedMotion ? 0 : 0.15,
                    duration: 0.2 
                  }}
                  className="text-gray-300 hover:text-white transition-colors font-medium flex items-center space-x-2 py-2 px-3 rounded-lg hover:bg-white/5 text-left"
                >
                  <Target className="w-4 h-4" />
                  <span>Try It Free</span>
                </motion.button>
                */}
                
                <motion.button 
                  onClick={() => { navigate('/onboarding?mode=login'); setMobileMenuOpen(false); }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    delay: prefersReducedMotion ? 0 : 0.2,
                    duration: 0.2 
                  }}
                  className="text-gray-300 hover:text-white transition-colors font-medium text-left py-2 px-3 rounded-lg hover:bg-white/5"
                >
                  Login
                </motion.button>
                
                <motion.button 
                  onClick={() => { navigate('/onboarding?mode=register'); setMobileMenuOpen(false); }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    delay: prefersReducedMotion ? 0 : 0.25,
                    duration: 0.2 
                  }}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all mt-2"
                >
                  Get Started
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Container */}
      <div
        className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 overflow-x-hidden"
        style={{
          // Mobile performance optimizations
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Shutdown Notice Banner */}
        <div className="w-full bg-amber-500/15 border-b border-amber-500/30 px-4 py-3 mt-16 sm:mt-20">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <p className="text-amber-300 text-sm">
              <span className="font-semibold">Rulevia is shutting down on July 14, 2026.</span>{' '}
              Thank you to all our users for your trust and support.
            </p>
          </div>
        </div>

        {/* Enhanced Hero Section - Mobile First */}
      <section className="pt-20 sm:pt-24 lg:pt-32 pb-6 sm:pb-8 lg:pb-10 px-4 sm:px-6" style={{ overflow: 'visible' }}>
        <div className="max-w-7xl mx-auto" style={{ overflow: 'visible' }}>
          <motion.div
            {...mobileAnimationConfig}
            className="text-center"
            style={{ overflow: 'visible', paddingBottom: '2rem' }}
          >
            {/* Trust Badge */}
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 sm:px-5 sm:py-2.5 mb-6">
              <Star className="w-4 h-4 text-yellow-400 mr-2" />
              <span className="text-sm text-gray-200">Trusted by 1,000+ marketers worldwide</span>
            </div>

            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6" 
              style={{ lineHeight: '1.1' }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              Create & Optimize AI-Powered Ads
              <div className="block mt-1 sm:mt-2 relative" style={{ minHeight: isMobile ? '50px' : '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={currentPhraseIndex}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ 
                      duration: prefersReducedMotion ? 0 : 0.6,
                      ease: [0.4, 0.0, 0.2, 1],
                      opacity: { duration: prefersReducedMotion ? 0 : 0.4 },
                      y: { duration: prefersReducedMotion ? 0 : 0.6 },
                      scale: { duration: prefersReducedMotion ? 0 : 0.5 }
                    }}
                    className={`flex items-center justify-center ${isMobile ? 'gap-2' : 'gap-3'} absolute`}
                    style={{ backfaceVisibility: 'hidden', transform: 'translateZ(0)', width: '100%' }}
                    aria-live="polite"
                  >
                    <span className={`${isMobile ? '' : 'whitespace-nowrap'} bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent`}>
                      {headlinePhrases[currentPhraseIndex].text}
                    </span>
                    {!isMobile && (
                      <motion.span
                        initial={{ rotate: -10, opacity: 0.7 }}
                        animate={{ rotate: 0, opacity: 0.9 }}
                        exit={{ rotate: 10, opacity: 0.7 }}
                        transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
                      >
                        {React.createElement(headlinePhrases[currentPhraseIndex].icon, {
                          className: "w-8 h-8 sm:w-10 sm:h-10 lg:w-14 lg:h-14 text-purple-400"
                        })}
                      </motion.span>
                    )}
                  </motion.span>
                </AnimatePresence>
              </div>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-4 max-w-3xl mx-auto leading-relaxed px-2 sm:px-0">
              Generate stunning ads from scratch or analyze existing ones. Get instant compliance checks and performance predictions 
              across Meta, Google, TikTok, YouTube, Pinterest, and Microsoft <span className="text-purple-400 font-semibold">before you launch</span>.
            </p>

            {/* Value Props */}
            <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm sm:text-base">
              <div className="flex items-center text-gray-300">
                <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                <span>AI Ad Generation</span>
              </div>
              <div className="flex items-center text-gray-300">
                <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                <span>3-Minute Analysis</span>
              </div>
              <div className="flex items-center text-gray-300">
                <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                <span>AI Enhancement</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={mobileAnimationConfig.initial}
            animate={mobileAnimationConfig.animate}
            transition={{ ...mobileAnimationConfig.transition, delay: prefersReducedMotion ? 0 : 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <button 
              onClick={() => navigate('/onboarding?mode=register')}
              className={`bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-xl text-base sm:text-lg lg:text-xl font-bold hover:from-purple-700 hover:to-blue-700 transition-all ${!isMobile ? 'transform hover:scale-105' : ''} flex items-center justify-center group shadow-2xl hover:shadow-purple-500/25 min-h-[48px]`}
            >
              Start Creating Ads - Free
              <ArrowRight className={`ml-3 w-5 h-5 ${!isMobile ? 'group-hover:translate-x-1' : ''} transition-transform`} />
            </button>
            <button 
              onClick={() => setShowUploadModal(true)}
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-xl text-base sm:text-lg lg:text-xl font-semibold hover:bg-white/20 hover:border-white/50 transition-all flex items-center justify-center group min-h-[48px]"
            >
              <FileSearch className="mr-3 w-5 h-5" />
              Analyze Your Ads
            </button>
          </motion.div>

          {/* Platform Logos - Enhanced Mobile Layout */}
          <motion.div
            initial={mobileAnimationConfig.initial}
            animate={mobileAnimationConfig.animate}
            transition={{ ...mobileAnimationConfig.transition, delay: prefersReducedMotion ? 0 : 0.4 }}
            className="mb-16"
          >
            <p className="text-gray-400 text-center mb-6">Instant compliance checking for all major platforms</p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {platforms.map((platform) => (
                <motion.div 
                  key={platform.name} 
                  className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg px-4 py-3 hover:bg-white/10 transition-all"
                  whileHover={prefersReducedMotion || isMobile ? {} : { scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <PlatformIcon platform={platform.key} className="w-6 h-6" />
                  <span className="text-gray-300 text-sm sm:text-base font-medium">{platform.name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain Points Section - NEW */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent to-black/20">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              The Hidden Cost of
              <span className="text-red-400"> Bad Creative Compliance</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Every day marketers lose money, time, and opportunities due to preventable issues
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {painPoints.map((pain, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-red-900/20 to-purple-900/20 backdrop-blur-lg rounded-2xl p-6 lg:p-8 border border-red-500/20 hover:border-red-500/40 transition-all group"
              >
                <pain.icon className="w-12 h-12 text-red-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-semibold text-white mb-3">{pain.title}</h3>
                <p className="text-gray-300 mb-4">{pain.description}</p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-2xl font-bold text-red-400">{pain.stat}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA after pain points */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-xl text-gray-300 mb-6">
              It doesn't have to be this way. Rulevia eliminates these problems completely.
            </p>
            <button 
              onClick={() => navigate('/onboarding?mode=register')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 inline-flex items-center group"
            >
              See How It Works
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits & Value Proposition Section - NEW */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 bg-black/10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            {...mobileAnimationConfig}
            whileInView={mobileAnimationConfig.animate}
            viewport={mobileAnimationConfig.viewport}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Rulevia?</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Everything you need to create winning ads that get approved and convert
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Benefit 1: AI Ad Generation */}
            <motion.div
              initial={mobileAnimationConfig.initial}
              whileInView={mobileAnimationConfig.animate}
              transition={{ ...mobileAnimationConfig.transition, delay: prefersReducedMotion ? 0 : 0.1 }}
              viewport={mobileAnimationConfig.viewport}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
            >
              <div className={`w-14 h-14 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 ${!isMobile ? 'group-hover:scale-110' : ''} transition-transform`}>
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">AI Ad Generation</h3>
              <p className="text-gray-300 mb-4">
                Create professional, compliant ads from scratch in seconds. Export-ready designs for any platform.
              </p>
              <div className="flex items-center text-purple-400">
                <Rocket className="w-5 h-5 mr-2" />
                <span className="text-sm">Professional ad creation</span>
              </div>
            </motion.div>

            {/* Benefit 2: Multi-Platform Compliance */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Multi-Platform Compliance</h3>
              <p className="text-gray-300 mb-4">
                Instant compliance checking across Meta, Google, TikTok, YouTube, Pinterest, and Microsoft Ads—all in one place.
              </p>
              <div className="flex flex-wrap gap-2">
                {platforms.slice(0, 3).map((platform) => (
                  <div key={platform.key} className="flex items-center bg-white/5 rounded-lg px-3 py-1.5 sm:py-2 min-h-[32px]">
                    <PlatformIcon platform={platform.key} className="w-4 h-4 mr-1" />
                    <span className="text-xs text-gray-300">{platform.name}</span>
                  </div>
                ))}
                <span className="text-xs text-gray-400 italic">+3 more</span>
              </div>
            </motion.div>

            {/* Benefit 3: AI Performance Predictions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">AI Performance Predictions</h3>
              <p className="text-gray-300 mb-4">
                Know exactly how your ads will perform before spending a dollar. Get platform-specific performance scores and recommendations.
              </p>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400">87%</div>
                  <div className="text-xs text-gray-400">Avg. Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">2.5x</div>
                  <div className="text-xs text-gray-400">Better CTR</div>
                </div>
              </div>
            </motion.div>

            {/* Benefit 4: Creative Enhancement */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Creative Enhancement</h3>
              <p className="text-gray-300 mb-4">
                AI-powered suggestions and automatic creative improvements for better performance across all platforms.
              </p>
              <div className="flex items-center text-green-400">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-sm">Instant optimization</span>
              </div>
            </motion.div>

            {/* Benefit 5: Complete Versioning */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <GitBranch className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Complete Versioning</h3>
              <p className="text-gray-300 mb-4">
                Track every iteration of your creatives with complete version history and rollback options.
              </p>
              <div className="flex items-center text-blue-400">
                <History className="w-5 h-5 mr-2" />
                <span className="text-sm">Never lose a version</span>
              </div>
            </motion.div>

            {/* Benefit 6: Team Collaboration */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Team Collaboration</h3>
              <p className="text-gray-300 mb-4">
                Invite teammates, manage permissions, and collaborate seamlessly across your entire organization.
              </p>
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-300">Unlimited collaboration</span>
              </div>
            </motion.div>
          </div>

          {/* CTA after benefits */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button 
              onClick={() => navigate('/onboarding?mode=register')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 inline-flex items-center group"
            >
              Start Optimizing Your Ads Today
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Before/After Comparison Section - NEW */}
      <section id="comparison" className="py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              See the 
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Rulevia Difference</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Real results from real marketers who transformed their creative workflow
            </p>
          </motion.div>

          {/* Before/After Comparison Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Before Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-red-900/20 to-gray-900/20 rounded-2xl p-6 lg:p-8 border border-red-500/20"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mr-4">
                  <ThumbsDown className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">Before Rulevia</h3>
              </div>
              
              <div className="space-y-4">
                {beforeAfterComparison.before.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <item.icon className={`w-5 h-5 ${item.color} flex-shrink-0 mt-0.5`} />
                    <p className="text-gray-300">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* After Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-900/20 to-blue-900/20 rounded-2xl p-6 lg:p-8 border border-green-500/20"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mr-4">
                  <ThumbsUp className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">After Rulevia</h3>
              </div>
              
              <div className="space-y-4">
                {beforeAfterComparison.after.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <item.icon className={`w-5 h-5 ${item.color} flex-shrink-0 mt-0.5`} />
                    <p className="text-gray-300">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Metrics Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {beforeAfterComparison.metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                  {metric.value}
                </p>
                <p className="text-white font-semibold">{metric.label}</p>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <button 
              onClick={() => navigate('/onboarding?mode=register')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 inline-flex items-center group shadow-xl"
            >
              Get These Results for Your Ads
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Section (Testimonials) - MOVED TO POSITION #3 */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 bg-black/15">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Loved by
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Marketers</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Join thousands of marketers who've transformed their creative workflow
            </p>
          </motion.div>

          {/* Testimonials Carousel */}
          <div className="relative max-w-4xl mx-auto">
            {/* Navigation Buttons */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute -left-16 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-105 hidden lg:flex"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToNextTestimonial}
              className="absolute -right-16 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-105 hidden lg:flex"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Responsive container with flexible height */}
            <div className="min-h-[420px] sm:min-h-[400px] lg:min-h-[420px] lg:max-h-[450px] relative overflow-hidden lg:overflow-y-auto lg:scrollbar-thin">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTestimonial}
                  initial={{ opacity: 0, x: isMobile ? 0 : 50 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    scale: isDragging ? 0.98 : 1
                  }}
                  exit={{ opacity: 0, x: isMobile ? 0 : -50 }}
                  transition={{ duration: prefersReducedMotion ? 0 : (isMobile ? 0.3 : 0.5) }}
                  drag="x"
                  dragConstraints={{ left: -200, right: 200 }}
                  dragElastic={0.1}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  whileDrag={{ cursor: "grabbing" }}
                  className="absolute inset-0 bg-white/5 backdrop-blur-lg rounded-2xl p-5 sm:p-8 lg:p-12 border border-white/10 cursor-grab active:cursor-grabbing flex flex-col justify-between"
                  style={{
                    opacity: isDragging ? 0.8 : 1,
                  }}
                >
                  <div className="flex-shrink-0">
                    <div className="flex items-center mb-3 sm:mb-6">
                      {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    
                    <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 italic leading-relaxed lg:leading-loose px-2 sm:px-0">
                      "{testimonials[activeTestimonial].content}"
                    </p>
                  </div>
                  
                  <div className="flex items-center mt-auto flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3 sm:mr-4 text-sm sm:text-base">
                      {testimonials[activeTestimonial].avatar}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm sm:text-base">{testimonials[activeTestimonial].name}</p>
                      <p className="text-gray-400 text-xs sm:text-sm">{testimonials[activeTestimonial].role}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

          </div>
          
          {/* Mobile Navigation Buttons */}
          <div className="flex justify-center mt-6 sm:hidden" style={{ gap: '12px' }}>
            <button
              onClick={goToPrevTestimonial}
              className="w-8 h-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToNextTestimonial}
              className="w-8 h-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Testimonial Navigation Dots */}
          <div className="flex justify-center mt-4" style={{ gap: '6px' }}>
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className="rounded-full transition-all"
                  style={{
                    width: index === activeTestimonial ? '12px' : '6px',
                    height: '6px',
                    background: index === activeTestimonial 
                      ? 'linear-gradient(to right, rgb(168 85 247), rgb(59 130 246))' 
                      : 'rgba(255, 255, 255, 0.3)',
                    border: 'none',
                    cursor: 'pointer',
                    minWidth: index === activeTestimonial ? '12px' : '6px',
                    minHeight: '6px'
                  }}
                />
              ))}
            </div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-400 mb-4">Trusted by 1,000+ marketers and growing</p>
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400">98%</div>
                <div className="text-sm text-gray-400">Approval Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">50K+</div>
                <div className="text-sm text-gray-400">Creatives Analyzed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">5.2x</div>
                <div className="text-sm text-gray-400">Average ROI</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Ad Generator Benefits Section - NEW */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-br from-purple-900/20 to-blue-900/20">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Create Ads 100x Faster with
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> AI Generator</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Stop paying thousands for designers. Generate unlimited ad variations instantly.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <DollarSign className="w-12 h-12 text-green-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Save $1000s on Design</h3>
              <p className="text-gray-300">
                No more expensive design agencies. Create professional ads for a fraction of the cost.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <Clock className="w-12 h-12 text-blue-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Generate in Seconds</h3>
              <p className="text-gray-300">
                What takes designers days, our AI creates in seconds. Launch campaigns faster than ever.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <Layers className="w-12 h-12 text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Unlimited Variations</h3>
              <p className="text-gray-300">
                Create multiple versions for A/B testing. Find what works best for your audience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <Palette className="w-12 h-12 text-pink-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Multiple Styles</h3>
              <p className="text-gray-300">
                Modern, minimalist, bold, playful - create ads in any style that matches your brand.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <Target className="w-12 h-12 text-orange-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Perfect for Testing</h3>
              <p className="text-gray-300">
                Quickly test different messages, visuals, and CTAs to optimize campaign performance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <History className="w-12 h-12 text-yellow-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Regenerate with Feedback</h3>
              <p className="text-gray-300">
                Not perfect? Provide feedback and regenerate until you get exactly what you want.
              </p>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <button 
              onClick={() => navigate('/onboarding?mode=register')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl text-lg font-bold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 inline-flex items-center group shadow-2xl hover:shadow-purple-500/25"
            >
              Start Generating Ads Now
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section - NEW */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              How It
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Works</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Two powerful workflows to create and optimize your ads
            </p>
            
            {/* Workflow Tabs */}
            <div className="flex justify-center mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-1 inline-flex">
                <button
                  onClick={() => setActiveWorkflowTab('create')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeWorkflowTab === 'create'
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Create New Ads
                </button>
                <button
                  onClick={() => setActiveWorkflowTab('analyze')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeWorkflowTab === 'analyze'
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Analyze Existing Ads
                </button>
              </div>
            </div>
          </motion.div>

          {/* Steps Grid - Conditional based on tab */}
          {activeWorkflowTab === 'create' ? (
            /* Create New Ads Workflow */
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1: Write Brief */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <FileText className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Write Brief</h3>
                  <p className="text-gray-300">
                    Describe your product, target audience, and campaign goals. AI understands your vision.
                  </p>
                </div>
                {/* Connector Line */}
                <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent"></div>
              </motion.div>

              {/* Step 2: Upload Assets */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Upload className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Upload Assets</h3>
                  <p className="text-gray-300">
                    Add your logo and reference images. Our AI uses them to create on-brand designs.
                  </p>
                </div>
                {/* Connector Line */}
                <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent"></div>
              </motion.div>

              {/* Step 3: AI Generates */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Sparkles className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">AI Generates</h3>
                  <p className="text-gray-300">
                    Watch AI create stunning, platform-optimized ads in seconds. Regenerate with feedback.
                  </p>
                </div>
                {/* Connector Line */}
                <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent"></div>
              </motion.div>

              {/* Step 4: Download & Analyze */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Target className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">4</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Download & Analyze</h3>
                  <p className="text-gray-300">
                    Download your ads and run compliance analysis to ensure perfect platform fit.
                  </p>
                </div>
              </motion.div>
            </div>
          ) : (
            /* Analyze Existing Ads Workflow */
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1: Upload Creative */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Upload className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Upload Creative</h3>
                  <p className="text-gray-300">
                    Simply drag and drop your image or video. Supports images up to 20MB, videos up to 1GB.
                  </p>
                </div>
                {/* Connector Line */}
                <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent"></div>
              </motion.div>

              {/* Step 2: Get AI Analysis */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Brain className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Get AI Analysis</h3>
                  <p className="text-gray-300">
                    Instant compliance check and performance prediction across all 6 major platforms.
                  </p>
                </div>
                {/* Connector Line */}
                <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent"></div>
              </motion.div>

              {/* Step 3: AI Enhancement */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Sparkles className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">AI Enhancement</h3>
                  <p className="text-gray-300">
                    Get AI-enhanced versions optimized for each platform with automatic versioning.
                  </p>
                </div>
                {/* Connector Line */}
                <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent"></div>
              </motion.div>

              {/* Step 4: Team Collaboration */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 relative">
                    <Users className="w-10 h-10 text-white" />
                    <span className="absolute -top-3 -right-3 bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">4</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Team Collaboration</h3>
                  <p className="text-gray-300">
                    Share with team, manage projects, and generate automated reports instantly.
                  </p>
                </div>
              </motion.div>
            </div>
          )}

          {/* Interactive Demo CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <p className="text-gray-300 mb-6">See it in action with our interactive demo</p>
            <button 
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 inline-flex items-center group"
            >
              <Play className="mr-3 w-5 h-5" />
              Try Interactive Demo
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Features Section - Enhanced */}
      <section id="features" className="py-16 lg:py-20 px-4 sm:px-6 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Everything You Need to
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Succeed</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              From compliance checking to AI enhancement, we've got every tool to make your creatives perform
            </p>
          </motion.div>

          {/* Feature Grid - Mobile Responsive */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`relative bg-white/5 backdrop-blur-lg rounded-2xl p-6 border transition-all group hover:scale-105 ${
                  feature.highlight 
                    ? 'border-purple-400/50 hover:border-purple-400' 
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                {feature.highlight && (
                  <div className="absolute -top-3 -right-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs px-3 py-1 rounded-full">
                    Popular
                  </div>
                )}
                <feature.icon className="w-10 h-10 text-purple-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>



      {/* Video Demo Section - NEW */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-black/10">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              See Rulevia in
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Action</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Watch how easy it is to validate and enhance your creatives
            </p>
          </motion.div>

          {/* Video Demo Placeholders */}
          <div className="space-y-16">
            {/* Demo 1: AI Ad Generation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center"
            >
              <div className="order-2 lg:order-1">
                <h3 className="text-2xl font-bold text-white mb-4">
                  <span className="text-purple-400">01.</span> AI Ad Generation
                </h3>
                <p className="text-gray-300 mb-6">
                  Create professional ads from scratch in seconds. Just provide your brief, upload your logo 
                  and reference images, and watch AI generate stunning, on-brand creatives.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-300">
                    <Sparkles className="w-5 h-5 text-purple-400 mr-3" />
                    Multiple styles and variations instantly
                  </li>
                  <li className="flex items-center text-gray-300">
                    <Target className="w-5 h-5 text-blue-400 mr-3" />
                    Platform-optimized designs automatically
                  </li>
                  <li className="flex items-center text-gray-300">
                    <RotateCcw className="w-5 h-5 text-green-400 mr-3" />
                    Regenerate with feedback until perfect
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <AutoplayVideo 
                  src="/Ad_generation_tutorial.webm"
                  className="aspect-video border border-purple-400/20 hover:border-purple-400/40 transition-all"
                  threshold={0.3}
                />
              </div>
            </motion.div>

            {/* Demo 2: Upload & Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center"
            >
              <div className="order-1 lg:order-1">
                <AutoplayVideo 
                  src="/Analysis_video_tutorial.webm"
                  className="aspect-video border border-purple-400/20 hover:border-purple-400/40 transition-all"
                  threshold={0.3}
                />
              </div>
              <div className="order-2 lg:order-2">
                <h3 className="text-2xl font-bold text-white mb-4">
                  <span className="text-purple-400">02.</span> Upload & Instant Analysis
                </h3>
                <p className="text-gray-300 mb-6">
                  Simply drag and drop your creative. Within seconds, get comprehensive compliance 
                  checks and performance predictions across all major platforms.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-300">
                    <FileImage className="w-5 h-5 text-blue-400 mr-3" />
                    Support for images up to 20MB, videos up to 1GB
                  </li>
                  <li className="flex items-center text-gray-300">
                    <Shield className="w-5 h-5 text-green-400 mr-3" />
                    Instant multi-platform compliance checking
                  </li>
                  <li className="flex items-center text-gray-300">
                    <Brain className="w-5 h-5 text-purple-400 mr-3" />
                    AI-powered performance predictions
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Demo 3: AI Enhancement */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center"
            >
              <div className="order-2 lg:order-1">
                <h3 className="text-2xl font-bold text-white mb-4">
                  <span className="text-purple-400">03.</span> AI-Powered Enhancement
                </h3>
                <p className="text-gray-300 mb-6">
                  Our AI doesn't just analyze—it improves. Get automatically enhanced versions 
                  of your creatives optimized for each platform's best practices.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-300">
                    <Sparkles className="w-5 h-5 text-purple-400 mr-3" />
                    Automatic text placement optimization
                  </li>
                  <li className="flex items-center text-gray-300">
                    <Sparkles className="w-5 h-5 text-purple-400 mr-3" />
                    Color and contrast improvements
                  </li>
                  <li className="flex items-center text-gray-300">
                    <Sparkles className="w-5 h-5 text-purple-400 mr-3" />
                    Platform-specific cropping and sizing
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <AutoplayVideo 
                  src="/Enhance_video_tutorial.webm"
                  className="aspect-video border border-purple-400/20 hover:border-purple-400/40 transition-all"
                  threshold={0.3}
                />
              </div>
            </motion.div>

            {/* Demo 4: Team Collaboration */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center"
            >
              <div className="order-2 lg:order-2">
                <h3 className="text-2xl font-bold text-white mb-4">
                  <span className="text-purple-400">04.</span> Team Collaboration & Versioning
                </h3>
                <p className="text-gray-300 mb-6">
                  Work seamlessly with your team. Track every version, share projects, 
                  and maintain a complete history of all creative iterations.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-300">
                    <Users className="w-5 h-5 text-blue-400 mr-3" />
                    Real-time team collaboration
                  </li>
                  <li className="flex items-center text-gray-300">
                    <GitBranch className="w-5 h-5 text-blue-400 mr-3" />
                    Complete version history
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FolderOpen className="w-5 h-5 text-blue-400 mr-3" />
                    Organized project management
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-1">
                <AutoplayVideo 
                  src="/Versioning_video_tutorial.webm"
                  className="aspect-video border border-purple-400/20 hover:border-purple-400/40 transition-all"
                  threshold={0.3}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Pricing Section - Enhanced Mobile */}
      <section id="pricing" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-black/5">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Simple, Transparent
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Pricing</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
              Start with a free trial, then choose the plan that fits your needs
            </p>
          </motion.div>

          {/* Pricing Plans - Two Row Layout */}
          
          {/* Row 1: Free Trial, Pro (Featured), Starter */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-12">
            {[pricingPlans[0], pricingPlans[2], pricingPlans[1]].map((plan, index) => {
              const originalIndex = pricingPlans.findIndex(p => p.name === plan.name);
              const isProPlan = plan.name === 'Pro';
              return (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className={`relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl rounded-3xl p-8 lg:p-12 border transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20 ${
                    plan.popular 
                      ? 'border-purple-400 ring-2 ring-purple-400/50 shadow-xl shadow-purple-500/25 transform scale-105' 
                      : 'border-white/20 hover:border-white/40'
                  } ${isProPlan ? 'lg:scale-110 lg:z-10' : ''}`}
                >
                {plan.popular && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">${plan.price}</span>
                    <span className="text-gray-400 text-lg">/{plan.period}</span>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 mb-6 space-y-3">
                    <div className="flex items-center justify-between px-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-purple-400" />
                        <span className="text-gray-300 text-sm font-medium">{plan.credits} analyses</span>
                      </div>
                      <span className="text-xs text-gray-400">per month</span>
                    </div>
                    <div className="flex items-center justify-between px-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span className="text-gray-300 text-sm font-medium">{plan.creativeCredits} creatives</span>
                      </div>
                      <span className="text-xs text-gray-400">per month</span>
                    </div>
                  </div>
                </div>

                <ul className="space-y-4 mb-10">
                  {(expandedPricingPlans.has(plan.name) ? plan.features : plan.features.slice(0, 4)).map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-4">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-200 text-base leading-relaxed font-medium">{feature}</span>
                    </li>
                  ))}
                  {plan.features.length > 4 && (
                    <li>
                      <button 
                        onClick={() => togglePricingPlan(plan.name)}
                        className="w-full text-center text-sm text-purple-300 font-medium bg-purple-500/10 hover:bg-purple-500/20 rounded-lg py-2 px-3 transition-colors duration-200 flex items-center justify-center space-x-2"
                      >
                        <span>
                          {expandedPricingPlans.has(plan.name) 
                            ? 'Show Less' 
                            : `+${plan.features.length - 4} more features`
                          }
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          expandedPricingPlans.has(plan.name) ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => {
                    if (plan.name === 'Free Trial') {
                      navigate('/onboarding?mode=register');
                    } else {
                      navigate('/onboarding?mode=register&plan=' + plan.name.toLowerCase());
                    }
                  }}
                  className={`w-full py-4 lg:py-5 px-8 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 text-lg shadow-lg hover:shadow-xl ${
                    plan.popular
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-purple-500/30 hover:shadow-purple-500/50'
                      : plan.name === 'Free Trial'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-green-500/30 hover:shadow-green-500/50'
                        : 'bg-white/10 text-white border-2 border-white/30 hover:bg-white/20 hover:border-white/50 backdrop-blur-sm'
                  }`}
                >
                  {plan.cta}
                </button>
                </motion.div>
              );
            })}
          </div>

          {/* Row 2: Agency, Enterprise */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto mb-16">
            {[pricingPlans[3], pricingPlans[4]].map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.15 }}
                viewport={{ once: true }}
                className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl rounded-3xl p-8 lg:p-12 border transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20 border-white/20 hover:border-white/40"
              >
                <div className="text-center mb-8">
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">${plan.price}</span>
                    <span className="text-gray-400 text-lg">/{plan.period}</span>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 mb-6 space-y-3">
                    <div className="flex items-center justify-between px-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-purple-400" />
                        <span className="text-gray-300 text-sm font-medium">{plan.credits} analyses</span>
                      </div>
                      <span className="text-xs text-gray-400">per month</span>
                    </div>
                    <div className="flex items-center justify-between px-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span className="text-gray-300 text-sm font-medium">{plan.creativeCredits} creatives</span>
                      </div>
                      <span className="text-xs text-gray-400">per month</span>
                    </div>
                  </div>
                </div>

                <ul className="space-y-4 mb-10">
                  {(expandedPricingPlans.has(plan.name) ? plan.features : plan.features.slice(0, 4)).map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-4">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-200 text-base leading-relaxed font-medium">{feature}</span>
                    </li>
                  ))}
                  {plan.features.length > 4 && (
                    <li>
                      <button 
                        onClick={() => togglePricingPlan(plan.name)}
                        className="w-full text-center text-sm text-purple-300 font-medium bg-purple-500/10 hover:bg-purple-500/20 rounded-lg py-2 px-3 transition-colors duration-200 flex items-center justify-center space-x-2"
                      >
                        <span>
                          {expandedPricingPlans.has(plan.name) 
                            ? 'Show Less' 
                            : `+${plan.features.length - 4} more features`
                          }
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          expandedPricingPlans.has(plan.name) ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => {
                    navigate('/onboarding?mode=register&plan=' + plan.name.toLowerCase());
                  }}
                  className="w-full py-4 lg:py-5 px-8 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 text-lg shadow-lg hover:shadow-xl bg-white/10 text-white border-2 border-white/30 hover:bg-white/20 hover:border-white/50 backdrop-blur-sm"
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-gray-300 mb-4">All plans include monthly credit refresh and full platform support</p>
            <p className="text-gray-400 text-sm">
              Need more than 1,000 analyses or custom solutions? <button 
                onClick={() => window.location.href = 'mailto:sales@rulevia.com?subject=Custom Plan Inquiry'}
                className="text-purple-400 hover:text-purple-300 font-semibold"
              >
                Contact Sales
              </button>
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - NEW */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 bg-black/20">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Frequently Asked
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Questions</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300">
              Everything you need to know about Rulevia
            </p>
          </motion.div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  {expandedFAQ === index ? (
                    <Minus className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  ) : (
                    <Plus className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  )}
                </button>
                
                <AnimatePresence>
                  {expandedFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: prefersReducedMotion ? 0 : (isMobile ? 0.2 : 0.3) }}
                    >
                      <p className="px-6 pb-4 text-gray-300">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Contact Support Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-12 p-8 bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-2xl border border-purple-400/20"
            id="contact-form"
          >
            <div className="text-center mb-8">
              <MessageSquare className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Still have questions?</h3>
              <p className="text-gray-300">Our support team is here to help</p>
            </div>

            {submitStatus.type === 'success' ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-white mb-2">Message Sent!</h4>
                <p className="text-gray-300 mb-6">{submitStatus.message}</p>
                <button
                  onClick={() => setSubmitStatus({ type: null, message: '' })}
                  className="text-purple-400 hover:text-purple-300 font-semibold"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-6">
                {submitStatus.type === 'error' && (
                  <div className="bg-red-900/20 border border-red-400/20 rounded-lg p-4 text-center">
                    <p className="text-red-300 text-sm">{submitStatus.message}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Your name *"
                        value={contactForm.name}
                        onChange={(e) => handleContactFormChange('name', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="your@email.com *"
                        value={contactForm.email}
                        onChange={(e) => handleContactFormChange('email', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <select
                        value={contactForm.category}
                        onChange={(e) => handleContactFormChange('category', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      >
                        <option value="general" className="bg-gray-800">General Inquiry</option>
                        <option value="technical" className="bg-gray-800">Technical Support</option>
                        <option value="billing" className="bg-gray-800">Billing Question</option>
                        <option value="feature" className="bg-gray-800">Feature Request</option>
                        <option value="bug" className="bg-gray-800">Bug Report</option>
                      </select>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Subject *"
                        value={contactForm.subject}
                        onChange={(e) => handleContactFormChange('subject', e.target.value)}
                        maxLength={200}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        rows={5}
                        placeholder="Tell us how we can help... *"
                        value={contactForm.message}
                        onChange={(e) => handleContactFormChange('message', e.target.value)}
                        maxLength={5000}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {contactForm.message.length}/5000 characters (min 10)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Message
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-3">
                    We typically respond within 24 hours
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>


      {/* Final Trust Block & CTA Section */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 bg-gradient-to-r from-purple-600/20 to-blue-600/20">
        <div className="max-w-6xl mx-auto">
          {/* Trust Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-12">
              The Numbers Speak for
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Themselves</span>
            </h2>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">98%</div>
                <div className="text-white font-semibold text-lg">Approval Rate</div>
                <div className="text-gray-400 text-sm">First-time submission</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">3min</div>
                <div className="text-white font-semibold text-lg">Analysis Time</div>
                <div className="text-gray-400 text-sm">Complete results</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">5.2x</div>
                <div className="text-white font-semibold text-lg">ROI</div>
                <div className="text-gray-400 text-sm">In 3 months</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">1000+</div>
                <div className="text-white font-semibold text-lg">Happy Marketers</div>
                <div className="text-gray-400 text-sm">And growing</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Award className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Create AND Optimize Your
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Ads?</span>
            </h3>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join 1000+ marketers who create compliant, high-performing ads in minutes with our AI-powered platform
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button 
                onClick={() => navigate('/onboarding?mode=register')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-10 py-5 rounded-xl text-xl font-bold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 flex items-center justify-center group shadow-2xl hover:shadow-purple-500/25"
              >
                Start Generating Ads - Free
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => setShowUploadModal(true)}
                className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-10 py-5 rounded-xl text-xl font-semibold hover:bg-white/20 hover:border-white/50 transition-all flex items-center justify-center group"
              >
                <FileSearch className="mr-3 w-6 h-6" />
                Try Analysis Demo
              </button>
            </div>

            <p className="text-gray-400">
              <span className="text-green-400 font-semibold">✓ No credit card required</span> • 
              <span className="text-green-400 font-semibold"> ✓ 3 free credits</span> • 
              <span className="text-green-400 font-semibold"> ✓ Generate & analyze ads</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer - Enhanced Mobile */}
      <footer className="py-12 px-4 sm:px-6 bg-black/10 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/Logo-Rulevia-White.svg" 
                  alt="Rulevia Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                AI-powered creative intelligence platform that ensures your ads are compliant and high-performing across all major platforms.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/blog')} className="text-gray-400 hover:text-white transition-colors text-left">Blog</button></li>
                {/* Hidden as requested - API Docs and Changelog buttons
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API Docs</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Changelog</a></li>
                */}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li>
                  <button 
                    onClick={() => {
                      const contactForm = document.getElementById('contact-form');
                      contactForm?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Contact Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/privacy');
                    }}
                    className="text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/terms');
                    }}
                    className="text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/cookies');
                    }}
                    className="text-gray-400 hover:text-white transition-colors text-left"
                  >
                    Cookie Policy
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 sm:mb-0">
              &copy; 2025 Rulevia. All rights reserved.
            </p>
            <div className="flex space-x-4 text-sm">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  cookieConsent.show();
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                Manage Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full shadow-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center z-40"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 w-full max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Verify Your Creative</h2>
                <button
                  onClick={closeUploadModal}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {!showRegistrationPrompt ? (
                <>
                  {!uploadedFile && !isAnalyzing && !showPreviewResults && (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-purple-400 transition-colors cursor-pointer"
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('file-upload')?.click()}
                    >
                      <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Your Creative</h3>
                      <p className="text-gray-600 mb-6">
                        Drag and drop your image or video file here, or click to browse
                      </p>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file);
                        }}
                        className="hidden"
                        id="file-upload"
                      />
                      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all cursor-pointer inline-block">
                        Choose File
                      </div>
                      <p className="text-sm text-gray-500 mt-4">
                        Supports images (JPG, PNG, GIF, WebP, HEIC, HEIF) up to 20MB, videos (MP4, MOV, AVI, WebM, etc.) up to 1GB
                      </p>
                    </div>
                  )}

                  {uploadedFile && uploadProgress < 100 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-4">
                        {uploadedFile.type.startsWith('video/') ? (
                          <FileVideo className="w-16 h-16 text-purple-600" />
                        ) : (
                          <FileImage className="w-16 h-16 text-blue-600" />
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Uploading...</h3>
                      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-gray-600">{uploadProgress}% complete</p>
                    </div>
                  )}

                  {isAnalyzing && (
                    <div className="text-center">
                      <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Analyzing Creative...</h3>
                      <p className="text-gray-600 mb-6">
                        Checking compliance and predicting performance across all platforms...
                      </p>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                        {platforms.map((platform) => (
                          <div key={platform.name} className="flex flex-col items-center space-y-2">
                            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                              <PlatformIcon platform={platform.key} className="w-5 h-5" />
                            </div>
                            <span className="text-xs text-gray-600">{platform.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {showPreviewResults && (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Analysis Complete!</h3>
                      
                      {/* Blurred Preview Results */}
                      <div className="relative mb-6">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 filter blur-sm">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="text-center">
                              <div className="text-3xl font-bold text-purple-600 mb-1">94%</div>
                              <div className="text-sm text-gray-600">Compliance Score</div>
                            </div>
                            <div className="text-center">
                              <div className="text-3xl font-bold text-blue-600 mb-1">87%</div>
                              <div className="text-sm text-gray-600">Performance Score</div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Meta</span>
                              <span className="text-sm font-bold">96%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">YouTube</span>
                              <span className="text-sm font-bold">91%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">TikTok</span>
                              <span className="text-sm font-bold">83%</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center">
                          <div className="text-center">
                            <Lock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                            <p className="text-gray-700 font-medium">Register to unlock full insights!</p>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-lg text-gray-600 mb-6">
                        See your creative's predicted ad performance and detailed compliance analysis—register to unlock full insights!
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Rocket className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to See Your Results?</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Create your free account to unlock detailed compliance analysis, performance predictions, and AI-powered recommendations.
                  </p>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">What you'll get:</h4>
                    <div className="grid md:grid-cols-2 gap-3 text-left">
                      <div className="flex items-center space-x-2">
                        <Target className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-gray-700">Compliance scores for all platforms</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Rocket className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-gray-700">Performance predictions</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Zap className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-gray-700">AI-powered recommendations</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-gray-700">Detailed benchmarking data</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => {
                        closeUploadModal();
                        navigate('/onboarding?mode=register');
                      }}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center group"
                    >
                      Create Free Account
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      onClick={() => {
                        closeUploadModal();
                        navigate('/onboarding?mode=login');
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Sign In
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    Already have an account? <button 
                      onClick={() => {
                        closeUploadModal();
                        navigate('/onboarding?mode=login');
                      }}
                      className="text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
}